import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { PublicationTypeE } from 'src/app/models/bookPartLink';
import { FavoritesFileI } from 'src/app/models/favorites';
import { NotificationMessageService } from 'src/app/services/notification_message.service';
import { FavoritesService } from 'src/app/online/favorites/service/favorites.service';
import { BookmarkPublicationAddToFolderDialogComponent } from '../bookmark-publication-add-to-folder-dialog/bookmark-publication-add-to-folder-dialog.component';
import { GuardService } from 'src/app/services/guard.service';

export enum BookmarkPublicationButtonDisplayStyleE {
    // Round icon-only button with a bookmark icon and a "favorited" badge, used on publication cards.
    BOOKMARK = 'bookmark',
    // Round Material mini-fab with a save icon, used in the PDF reader's action bar.
    MINI_FAB = 'miniFab',
    // Rectangular text button with a save icon and label, used on the document content page.
    TEXT_BUTTON = 'textButton'
}

@Component({
    selector: 'app-bookmark-publication-button',
    templateUrl: './bookmark-publication-button.component.html',
    styleUrls: ['./bookmark-publication-button.component.scss']
})
export class BookmarkPublicationButtonComponent implements OnInit {
    readonly BookmarkPublicationButtonDisplayStyleE = BookmarkPublicationButtonDisplayStyleE;

    @Input() displayStyle: BookmarkPublicationButtonDisplayStyleE = BookmarkPublicationButtonDisplayStyleE.BOOKMARK;
    @Input() small = false;
    @Input() customClass = '';
    @Input() customStyle: { [key: string]: string | number } = {};
    @Input() publicationId: number;
    @Input() publicationType: PublicationTypeE;
    @Input() periodic = false;
    // The book the publication belongs to, when it is a book part - lets the user favorite/unfavorite the whole
    // book in addition to this specific chapter.
    @Input() bookId?: number;
    @Output() addToProject = new EventEmitter<void>();

    favorite$: Observable<FavoritesFileI | undefined>;
    favoriteBook$: Observable<FavoritesFileI | undefined>;

    addingToFavorites = false;
    addingWholeBookToFavorites = false;
    removingFromFavorites = false;

    constructor(
        private favoritesService: FavoritesService,
        private notificationMessageService: NotificationMessageService,
        private matDialog: MatDialog,
        private guardService: GuardService
    ) {}

    ngOnInit(): void {
        if (this.guardService.checkAuthorizedToAccessRoutePath('o/favorites')) {
            this.favorite$ = this.favoritesService.selectFavoriteByPublication$(
                this.publicationId,
                this.publicationType
            );
            this.favoriteBook$ = this.canAddWholeBook
                ? this.favoritesService.selectFavoriteByPublication$(this.bookId, PublicationTypeE.BOOK)
                : of(undefined);
        }
    }

    get canAddWholeBook(): boolean {
        return this.publicationType === PublicationTypeE.BOOKPART && !!this.bookId;
    }

    get isBookPart(): boolean {
        return this.publicationType === PublicationTypeE.BOOKPART;
    }

    get addOnlyThisPartLabelKey(): string {
        return this.periodic ? 'global.action.addOnlyThisArticle' : 'global.action.addOnlyThisChapter';
    }

    get removeThisPartLabelKey(): string {
        return this.periodic
            ? 'global.action.removeThisArticleFromFavorites'
            : 'global.action.removeThisChapterFromFavorites';
    }

    get addWholeBookLabelKey(): string {
        return this.periodic ? 'global.action.addWholeJournalToFavorites' : 'global.action.addWholeBookToFavorites';
    }

    get removeWholeBookLabelKey(): string {
        return this.periodic
            ? 'global.action.removeWholeJournalFromFavorites'
            : 'global.action.removeWholeBookFromFavorites';
    }

    get miniFabStyle(): { [key: string]: string | number } {
        return { top: '0', right: '0', ...this.customStyle };
    }

    onTriggerClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    onAddToFavoritesClick(event: Event): void {
        event.preventDefault();
        if (this.addingToFavorites) {
            return;
        }
        this.addingToFavorites = true;
        this.favoritesService
            .addPublicationToFavorites({
                publicationId: this.publicationId,
                publicationType: this.publicationType,
                favoritesFolderId: null
            })
            .subscribe({
                next: () => {
                    this.addingToFavorites = false;
                    this.notificationMessageService.init(
                        'favorites.add.success.title',
                        'favorites.add.success.message'
                    );
                    this.notificationMessageService.show();
                },
                error: () => (this.addingToFavorites = false)
            });
    }

    onAddWholeBookToFavoritesClick(event: Event): void {
        event.preventDefault();
        if (this.addingWholeBookToFavorites || !this.bookId) {
            return;
        }
        this.addingWholeBookToFavorites = true;
        this.favoritesService
            .addPublicationToFavorites({
                publicationId: this.bookId,
                publicationType: PublicationTypeE.BOOK,
                favoritesFolderId: null
            })
            .subscribe({
                next: () => {
                    this.addingWholeBookToFavorites = false;
                    this.notificationMessageService.init(
                        'favorites.add.success.title',
                        'favorites.add.success.message'
                    );
                    this.notificationMessageService.show();
                },
                error: () => (this.addingWholeBookToFavorites = false)
            });
    }

    onRemoveFromFavoritesClick(event: Event, favorite: FavoritesFileI): void {
        event.preventDefault();
        if (this.removingFromFavorites) {
            return;
        }
        this.removingFromFavorites = true;
        this.favoritesService.removeFavorite(favorite.id).subscribe({
            next: () => {
                this.removingFromFavorites = false;
                this.notificationMessageService.init(
                    'favorites.remove.success.title',
                    'favorites.remove.success.message'
                );
                this.notificationMessageService.show();
            },
            error: () => (this.removingFromFavorites = false)
        });
    }

    onAddToFavoritesFolderClick(event: Event): void {
        event.preventDefault();
        this.matDialog
            .open(BookmarkPublicationAddToFolderDialogComponent, {
                data: {
                    mode: 'add',
                    publicationId: this.publicationId,
                    publicationType: this.publicationType,
                    bookId: this.bookId,
                    periodic: this.periodic
                }
            })
            .afterClosed()
            .subscribe(added => {
                if (added) {
                    this.notificationMessageService.init(
                        'favorites.addToFolder.success.title',
                        'favorites.addToFolder.success.message'
                    );
                    this.notificationMessageService.show();
                }
            });
    }

    onMoveToFolderClick(event: Event, favorite: FavoritesFileI): void {
        event.preventDefault();
        this.matDialog
            .open(BookmarkPublicationAddToFolderDialogComponent, {
                data: {
                    mode: 'move',
                    favoritesFileId: favorite.id,
                    currentFolderId: favorite.favoritesFolderId
                }
            })
            .afterClosed()
            .subscribe(moved => {
                if (moved) {
                    this.notificationMessageService.init(
                        'favorites.moveToFolder.success.title',
                        'favorites.moveToFolder.success.message'
                    );
                    this.notificationMessageService.show();
                }
            });
    }

    onAddToProjectClick(event: Event): void {
        event.preventDefault();
        this.addToProject.emit();
    }
}
